import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input, TextArea } from '../../components/ui/Input';
import { useAssessment } from '../../hooks/useAssessment';
import { EU_AI_ACT_OBLIGATIONS } from '../../utils/euAiAct';
import { CheckCircle, AlertTriangle, XCircle, FileText, TrendingUp, Calendar, User, Plus, ChevronDown, ChevronUp, Download, ArrowRight } from 'lucide-react';
export function GapAnalysis() {
  const navigate = useNavigate();
  const {
    assessment,
    addRemediationAction,
    updateRemediationAction
  } = useAssessment();
  const [complianceResults, setComplianceResults] = useState<ComplianceResult[]>([]);
  const [expandedGaps, setExpandedGaps] = useState<Set<string>>(new Set());
  const [showNewAction, setShowNewAction] = useState<string | null>(null);
  // New action form state
  const [actionForm, setActionForm] = useState({
    description: '',
    owner: '',
    dueDate: '',
    notes: ''
  });
  // Calculate compliance results when evidence changes
  useEffect(() => {
    const results = calculateCompliance();
    setComplianceResults(results);
  }, [assessment.evidence]);
  const calculateCompliance = (): ComplianceResult[] => {
    const applicableObligations = EU_AI_ACT_OBLIGATIONS.filter(obl => assessment.riskLevel && obl.riskLevel.includes(assessment.riskLevel));
    return applicableObligations.map(obligation => {
      const linkedEvidence = assessment.evidence.filter(e => e.linkedObligations.includes(obligation.id));
      let status: ComplianceStatus;
      let explanation: string;
      let recommendation: string;
      if (linkedEvidence.length === 0) {
        status = 'not-evidenced';
        explanation = `No evidence has been provided for this obligation. The system cannot demonstrate compliance with ${obligation.title}.`;
        recommendation = getRecommendation(obligation.id);
      } else if (linkedEvidence.length === 1) {
        status = 'partially-evidenced';
        explanation = `One piece of evidence (${linkedEvidence[0].fileName}) has been linked to this obligation. Additional documentation is recommended to demonstrate comprehensive compliance.`;
        recommendation = `Provide supplementary evidence such as: ${getSupplementaryEvidence(obligation.id)}`;
      } else {
        status = 'fully-evidenced';
        explanation = `${linkedEvidence.length} pieces of evidence have been provided, covering multiple aspects of ${obligation.title}. This demonstrates a documented approach to compliance.`;
        recommendation = 'Maintain current evidence and update as the system evolves.';
      }
      return {
        obligationId: obligation.id,
        status,
        evidenceCount: linkedEvidence.length,
        linkedEvidence: linkedEvidence.map(e => e.fileName),
        explanation,
        recommendation
      };
    });
  };
  const getRecommendation = (obligationId: string): string => {
    const recommendations: Record<string, string> = {
      'OBL-001': 'Establish and document a risk management system. Required: risk assessment methodology, risk register with identified risks, mitigation strategies, and periodic review procedures.',
      'OBL-002': 'Document data governance practices. Required: dataset descriptions, data quality assessment reports, bias analysis, data lineage documentation, and validation procedures.',
      'OBL-003': 'Create comprehensive technical documentation. Required: system architecture diagrams, model specifications, training procedures, performance metrics, and version control records.',
      'OBL-004': 'Implement logging and record-keeping systems. Required: log retention policies, audit trail specifications, event logging procedures, and monitoring dashboards.',
      'OBL-005': 'Establish transparency mechanisms. Required: model explainability documentation, user-facing documentation, output interpretation guides, and transparency disclosures.',
      'OBL-006': 'Define human oversight procedures. Required: oversight protocols, human intervention procedures, escalation paths, and human review processes.',
      'OBL-007': 'Demonstrate accuracy, robustness, and security. Required: accuracy testing results, robustness assessments, cybersecurity audit reports, and incident response procedures.',
      'OBL-008': 'Complete EU database registration. Required: registration confirmation, compliance certificates, and ongoing reporting procedures.'
    };
    return recommendations[obligationId] || 'Provide relevant documentation to demonstrate compliance.';
  };
  const getSupplementaryEvidence = (obligationId: string): string => {
    const supplementary: Record<string, string> = {
      'OBL-001': 'risk mitigation plans, review meeting minutes, risk owner assignments',
      'OBL-002': 'data quality metrics, bias mitigation strategies, data lineage diagrams',
      'OBL-003': 'version control logs, change management records, testing protocols',
      'OBL-004': 'log analysis reports, monitoring dashboards, retention schedules',
      'OBL-005': 'user guides, explainability reports, transparency statements',
      'OBL-006': 'oversight committee charters, intervention logs, review procedures',
      'OBL-007': 'penetration test results, vulnerability assessments, security policies',
      'OBL-008': 'registration certificates, compliance attestations, audit reports'
    };
    return supplementary[obligationId] || 'additional supporting documentation';
  };
  const toggleGapExpansion = (obligationId: string) => {
    setExpandedGaps(prev => {
      const next = new Set(prev);
      if (next.has(obligationId)) {
        next.delete(obligationId);
      } else {
        next.add(obligationId);
      }
      return next;
    });
  };
  const handleCreateAction = (obligationId: string) => {
    const newAction: RemediationAction = {
      id: crypto.randomUUID(),
      obligationId,
      description: actionForm.description,
      owner: actionForm.owner,
      status: 'open',
      dueDate: actionForm.dueDate,
      notes: actionForm.notes,
      createdAt: new Date().toISOString()
    };
    addRemediationAction(newAction);
    setShowNewAction(null);
    setActionForm({
      description: '',
      owner: '',
      dueDate: '',
      notes: ''
    });
  };
  const handleUpdateActionStatus = (actionId: string, status: RemediationStatus) => {
    updateRemediationAction(actionId, status);
  };
  const getObligationActions = (obligationId: string): RemediationAction[] => {
    return (assessment.remediationActions || []).filter(a => a.obligationId === obligationId);
  };
  // Calculate summary statistics
  const fullyEvidenced = complianceResults.filter(r => r.status === 'fully-evidenced').length;
  const partiallyEvidenced = complianceResults.filter(r => r.status === 'partially-evidenced').length;
  const notEvidenced = complianceResults.filter(r => r.status === 'not-evidenced').length;
  const totalObligations = complianceResults.length;
  const complianceScore = totalObligations > 0 ? Math.round(fullyEvidenced / totalObligations * 100) : 0;
  const remediationActions = assessment.remediationActions || [];
  const openActions = remediationActions.filter(a => a.status === 'open').length;
  const inProgressActions = remediationActions.filter(a => a.status === 'in-progress').length;
  const completedActions = remediationActions.filter(a => a.status === 'completed').length;
  return <div className="max-w-7xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-360">
      {/* Header */}
      <div className="mb-8" data-id="element-361">
        <h1 className="text-3xl font-bold mb-3" data-id="element-362">Compliance Analysis</h1>
        <p className="text-slate-400 text-lg" data-id="element-363">
          Assessment complete. Review your compliance posture, identify gaps,
          and plan remediation.
        </p>
      </div>

      {/* Compliance Posture Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-id="element-364">
        <Card className="border-teal-500/30 bg-teal-500/5" data-id="element-365">
          <div className="text-center" data-id="element-366">
            <div className="text-4xl font-bold text-teal-400 mb-2" data-id="element-367">
              {complianceScore}%
            </div>
            <div className="text-sm text-slate-400" data-id="element-368">Compliance Score</div>
          </div>
        </Card>

        <Card className="border-teal-500/20" data-id="element-369">
          <div className="flex items-center justify-between" data-id="element-370">
            <div data-id="element-371">
              <div className="text-2xl font-bold text-teal-400" data-id="element-372">
                {fullyEvidenced}
              </div>
              <div className="text-xs text-slate-400" data-id="element-373">Fully Evidenced</div>
            </div>
            <CheckCircle className="h-8 w-8 text-teal-500" data-id="element-374" />
          </div>
        </Card>

        <Card className="border-amber-500/20" data-id="element-375">
          <div className="flex items-center justify-between" data-id="element-376">
            <div data-id="element-377">
              <div className="text-2xl font-bold text-amber-400" data-id="element-378">
                {partiallyEvidenced}
              </div>
              <div className="text-xs text-slate-400" data-id="element-379">Partially Evidenced</div>
            </div>
            <AlertTriangle className="h-8 w-8 text-amber-500" data-id="element-380" />
          </div>
        </Card>

        <Card className="border-red-500/20" data-id="element-381">
          <div className="flex items-center justify-between" data-id="element-382">
            <div data-id="element-383">
              <div className="text-2xl font-bold text-red-400" data-id="element-384">
                {notEvidenced}
              </div>
              <div className="text-xs text-slate-400" data-id="element-385">Not Evidenced</div>
            </div>
            <XCircle className="h-8 w-8 text-red-500" data-id="element-386" />
          </div>
        </Card>
      </div>

      {/* Remediation Progress */}
      {remediationActions.length > 0 && <Card className="mb-8 border-teal-500/30 hover:border-teal-500/50 transition-all cursor-pointer" onClick={() => navigate('/assessment/remediation')} data-id="element-387">
          <div className="flex items-center justify-between mb-4" data-id="element-388">
            <h3 className="text-lg font-semibold text-white flex items-center" data-id="element-389">
              <TrendingUp className="h-5 w-5 mr-2 text-teal-500" data-id="element-390" />
              Remediation Progress
            </h3>
            <div className="flex items-center space-x-3" data-id="element-391">
              <div className="text-sm text-slate-400" data-id="element-392">
                {completedActions} of {remediationActions.length} actions
                completed
              </div>
              <ArrowRight className="h-5 w-5 text-teal-400" data-id="element-393" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4" data-id="element-394">
            <div className="text-center p-4 bg-navy-900 rounded-lg border border-slate-800" data-id="element-395">
              <div className="text-2xl font-bold text-slate-300" data-id="element-396">
                {openActions}
              </div>
              <div className="text-xs text-slate-500" data-id="element-397">Open</div>
            </div>
            <div className="text-center p-4 bg-navy-900 rounded-lg border border-amber-500/20" data-id="element-398">
              <div className="text-2xl font-bold text-amber-400" data-id="element-399">
                {inProgressActions}
              </div>
              <div className="text-xs text-slate-500" data-id="element-400">In Progress</div>
            </div>
            <div className="text-center p-4 bg-navy-900 rounded-lg border border-teal-500/20" data-id="element-401">
              <div className="text-2xl font-bold text-teal-400" data-id="element-402">
                {completedActions}
              </div>
              <div className="text-xs text-slate-500" data-id="element-403">Completed</div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-slate-800 text-center" data-id="element-404">
            <span className="text-sm text-teal-400 font-medium" data-id="element-405">
              Click to view all remediation actions →
            </span>
          </div>
        </Card>}

      {/* Detailed Results */}
      <div className="space-y-4 mb-8" data-id="element-406">
        <div className="flex items-center justify-between" data-id="element-407">
          <h2 className="text-xl font-semibold text-white" data-id="element-408">
            Obligation-by-Obligation Analysis
          </h2>
          <Button variant="ghost" size="sm" data-id="element-409">
            <Download className="h-4 w-4 mr-2" data-id="element-410" />
            Export Report
          </Button>
        </div>

        {complianceResults.map(result => {
        const obligation = EU_AI_ACT_OBLIGATIONS.find(o => o.id === result.obligationId);
        if (!obligation) return null;
        const isExpanded = expandedGaps.has(result.obligationId);
        const actions = getObligationActions(result.obligationId);
        const isShowingNewAction = showNewAction === result.obligationId;
        return <Card key={result.obligationId} className={`transition-all ${result.status === 'fully-evidenced' ? 'border-teal-500/30 bg-teal-500/5' : result.status === 'partially-evidenced' ? 'border-amber-500/30 bg-amber-500/5' : 'border-red-500/30 bg-red-500/5'}`} data-id="element-411">
              {/* Header */}
              <div className="flex items-start justify-between mb-4" data-id="element-412">
                <div className="flex-1" data-id="element-413">
                  <div className="flex items-center space-x-3 mb-2" data-id="element-414">
                    <span className="font-mono text-sm font-semibold text-slate-500" data-id="element-415">
                      {obligation.id}
                    </span>
                    <Badge variant={result.status === 'fully-evidenced' ? 'teal' : result.status === 'partially-evidenced' ? 'amber' : 'red'} data-id="element-416">
                      {result.status === 'fully-evidenced' ? 'Fully Evidenced' : result.status === 'partially-evidenced' ? 'Partially Evidenced' : 'Not Evidenced'}
                    </Badge>
                    <Badge variant="slate" className="text-xs" data-id="element-417">
                      {obligation.category}
                    </Badge>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-1" data-id="element-418">
                    {obligation.title}
                  </h3>
                  <p className="text-sm text-slate-400" data-id="element-419">
                    {obligation.description}
                  </p>
                </div>
                {result.status === 'fully-evidenced' ? <CheckCircle className="h-6 w-6 text-teal-500 flex-shrink-0 ml-4" data-id="element-420" /> : result.status === 'partially-evidenced' ? <AlertTriangle className="h-6 w-6 text-amber-500 flex-shrink-0 ml-4" data-id="element-421" /> : <XCircle className="h-6 w-6 text-red-500 flex-shrink-0 ml-4" data-id="element-422" />}
              </div>

              {/* Assessment Explanation */}
              <div className="mb-4 p-4 bg-navy-900 rounded-lg border border-slate-800" data-id="element-423">
                <div className="text-xs font-medium text-slate-400 mb-2" data-id="element-424">
                  Assessment Basis
                </div>
                <p className="text-sm text-slate-300 leading-relaxed" data-id="element-425">
                  {result.explanation}
                </p>
              </div>

              {/* Linked Evidence */}
              {result.linkedEvidence.length > 0 && <div className="mb-4" data-id="element-426">
                  <div className="text-xs font-medium text-slate-400 mb-2" data-id="element-427">
                    Linked Evidence ({result.evidenceCount})
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-428">
                    {result.linkedEvidence.map((fileName, idx) => <div key={idx} className="flex items-center text-xs bg-navy-900 px-3 py-1.5 rounded border border-slate-800" data-id="element-429">
                        <FileText className="h-3 w-3 mr-2 text-teal-500" data-id="element-430" />
                        <span className="text-slate-300" data-id="element-431">{fileName}</span>
                      </div>)}
                  </div>
                </div>}

              {/* Gap Details (expandable for non-compliant) */}
              {result.status !== 'fully-evidenced' && <div className="border-t border-slate-800 pt-4" data-id="element-432">
                  <button onClick={() => toggleGapExpansion(result.obligationId)} className="flex items-center justify-between w-full text-left mb-3 hover:text-teal-400 transition-colors" data-id="element-433">
                    <span className="text-sm font-medium text-white" data-id="element-434">
                      {result.status === 'partially-evidenced' ? 'Strengthen Evidence' : 'Close Gap'}
                    </span>
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" data-id="element-435" /> : <ChevronDown className="h-4 w-4 text-slate-400" data-id="element-436" />}
                  </button>

                  {isExpanded && <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-437">
                      {/* Recommendation */}
                      <div className={`p-4 rounded-lg border ${result.status === 'partially-evidenced' ? 'bg-amber-500/10 border-amber-500/20' : 'bg-red-500/10 border-red-500/20'}`} data-id="element-438">
                        <div className="text-xs font-medium text-slate-300 mb-2" data-id="element-439">
                          Recommended Action
                        </div>
                        <p className="text-sm text-slate-300 leading-relaxed" data-id="element-440">
                          {result.recommendation}
                        </p>
                      </div>

                      {/* Remediation Actions */}
                      {actions.length > 0 && <div data-id="element-441">
                          <div className="text-xs font-medium text-slate-400 mb-3" data-id="element-442">
                            Remediation Actions
                          </div>
                          <div className="space-y-2" data-id="element-443">
                            {actions.map(action => <div key={action.id} className="p-3 bg-navy-900 rounded-lg border border-slate-800" data-id="element-444">
                                <div className="flex items-start justify-between mb-2" data-id="element-445">
                                  <div className="flex-1" data-id="element-446">
                                    <div className="text-sm text-white font-medium mb-1" data-id="element-447">
                                      {action.description}
                                    </div>
                                    <div className="flex items-center space-x-3 text-xs text-slate-400" data-id="element-448">
                                      <span className="flex items-center" data-id="element-449">
                                        <User className="h-3 w-3 mr-1" data-id="element-450" />
                                        {action.owner}
                                      </span>
                                      {action.dueDate && <span className="flex items-center" data-id="element-451">
                                          <Calendar className="h-3 w-3 mr-1" data-id="element-452" />
                                          {new Date(action.dueDate).toLocaleDateString()}
                                        </span>}
                                    </div>
                                  </div>
                                  <select value={action.status} onChange={e => handleUpdateActionStatus(action.id, e.target.value as RemediationStatus)} className="text-xs bg-navy-800 border border-slate-700 rounded px-2 py-1 text-slate-300" data-id="element-453">
                                    <option value="open" data-id="element-454">Open</option>
                                    <option value="in-progress" data-id="element-455">
                                      In Progress
                                    </option>
                                    <option value="completed" data-id="element-456">Completed</option>
                                  </select>
                                </div>
                                {action.notes && <p className="text-xs text-slate-500 mt-2" data-id="element-457">
                                    {action.notes}
                                  </p>}
                              </div>)}
                          </div>
                        </div>}

                      {/* Add New Action */}
                      {!isShowingNewAction ? <Button variant="ghost" size="sm" onClick={() => setShowNewAction(result.obligationId)} className="w-full" data-id="element-458">
                          <Plus className="h-4 w-4 mr-2" data-id="element-459" />
                          Create Remediation Action
                        </Button> : <div className="p-4 bg-navy-900 rounded-lg border border-teal-500/30 space-y-3" data-id="element-460">
                          <div className="text-sm font-medium text-white mb-3" data-id="element-461">
                            New Remediation Action
                          </div>
                          <Input label="Action Description" placeholder="What needs to be done?" value={actionForm.description} onChange={e => setActionForm(prev => ({
                  ...prev,
                  description: e.target.value
                }))} required data-id="element-462" />
                          <div className="grid grid-cols-2 gap-3" data-id="element-463">
                            <Input label="Owner" placeholder="Who is responsible?" value={actionForm.owner} onChange={e => setActionForm(prev => ({
                    ...prev,
                    owner: e.target.value
                  }))} required data-id="element-464" />
                            <Input label="Due Date" type="date" value={actionForm.dueDate} onChange={e => setActionForm(prev => ({
                    ...prev,
                    dueDate: e.target.value
                  }))} data-id="element-465" />
                          </div>
                          <TextArea label="Notes" placeholder="Additional context or requirements..." rows={2} value={actionForm.notes} onChange={e => setActionForm(prev => ({
                  ...prev,
                  notes: e.target.value
                }))} data-id="element-466" />
                          <div className="flex space-x-2" data-id="element-467">
                            <Button size="sm" onClick={() => handleCreateAction(result.obligationId)} disabled={!actionForm.description || !actionForm.owner} data-id="element-468">
                              Create Action
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => {
                    setShowNewAction(null);
                    setActionForm({
                      description: '',
                      owner: '',
                      dueDate: '',
                      notes: ''
                    });
                  }} data-id="element-469">
                              Cancel
                            </Button>
                          </div>
                        </div>}
                    </div>}
                </div>}
            </Card>;
      })}
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-8 border-t border-slate-800" data-id="element-470">
        <Button variant="ghost" onClick={() => navigate('/assessment/evidence')} data-id="element-471">
          Back to Evidence
        </Button>
        <Button onClick={() => navigate('/assessment/audit-trail')} data-id="element-472">
          View Audit Trail
        </Button>
      </div>
    </div>;
}